import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SiteId, TaskId } from '@interfaces/IScheduler';
import { useSchedulerActions, useSchedulerStale, useSchedulerTask } from '../../hooks/useSchedulerStore';
import { useTaskCommentsPersist } from '../../hooks/useTaskCommentsPersist';
import { useCommentPhotoUploads } from '../../hooks/useCommentPhotoUploads';
import { CommentPhoto, PendingCommentPhoto } from './CommentPhotos';
import { useCurrentActor } from '@hooks/useCurrentActor';
import styles from './TaskPanelCommentsTab.module.css';
import { useSelector } from 'react-redux';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { useSchedulerCapabilities } from '../../context/SchedulerCapabilities';
import { siteStamp } from '@d-orca/shared/client/utils';
import { PHOTO_ACCEPT, PHOTO_REFUSAL_KEY } from '../../lib/photoRefusal';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

interface ICommentTimeProps {
  at: string;
  tz: string;
}

/** Relative while recent, the site's absolute time after — and that absolute
 *  time on hover either way, so "18 hours ago" can be pinned to its moment. */
function CommentTime({ at, tz }: ICommentTimeProps) {
  const { i18n } = useTranslation();
  const stamp = siteStamp(at, tz, i18n.language);
  return <time className={styles.time} dateTime={at} title={stamp.title}>{stamp.text}</time>;
}

interface ITaskPanelCommentsTabProps {
  taskId: TaskId;
  siteId: SiteId;
  /** Loaded schedule number — enables the immediate comment POST. Absent → the
   *  store still updates optimistically (Router-free tests). */
  scheduleNumber?: string;
}

export function TaskPanelCommentsTab({ taskId, siteId, scheduleNumber }: ITaskPanelCommentsTabProps) {
  const { t } = useTranslation();
  const actor = useCurrentActor();
  // Absolute stamps fall back to the site's zone, not the viewer's.
  const tz = useSelector(selectSiteTz);
  const { taskCommentPosted } = useSchedulerActions();
  // Read the LIVE composed task: comments are execution-owned + immediate-write
  // now, so the drawer's draft snapshot would go stale as posts land.
  const task = useSchedulerTask(siteId, taskId);
  const stale = useSchedulerStale(siteId);
  // Reading the thread is open to anyone who can open the task; posting is `tasks.execute` and the composer is not rendered without it.
  const { canExecute } = useSchedulerCapabilities();
  const post = useTaskCommentsPersist(siteId, scheduleNumber, taskId);
  const [text, setText] = useState('');
  // Photos go up the moment they are picked; the comment carries what was stored.
  const photos = useCommentPhotoUploads(scheduleNumber ? { siteId, scheduleNumber, taskId } : undefined);
  const fileRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const comments = task?.comments;

  useEffect(() => {
    const el = listRef.current;
    if (el && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight });
    }
  }, [comments?.length]);

  const canSend = !photos.uploading && (text.trim().length > 0 || photos.attachments.length > 0);

  function send() {
    if (!canSend || !task) return;
    if (stale) return; // frozen while behind upstream — no dispatch, no POST
    const commentId = `cm-${crypto.randomUUID()}`;
    taskCommentPosted({
      siteId, taskId,
      commentId,
      at: new Date().toISOString(),
      authorName: actor.name,
      authorId: actor.id,
      text: text.trim(),
      attachments: photos.attachments,
    });
    // Immediate-write: POST straight to the granular endpoint (leaves the Save path).
    // No author in the body — the server stamps it from the session.
    post({ id: commentId, text: text.trim(), attachments: photos.attachments });
    setText('');
    photos.reset();
  }

  function onPickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    photos.add?.(Array.from(e.target.files ?? []));
    e.target.value = '';
  }

  return (
    <div className={styles.wrap}>
      <ScrollArea className={styles.thread} viewportClassName={styles.threadContent} viewportRef={listRef}>
        {(comments ?? []).length === 0
          ? <p className={styles.empty}>{t('scheduler.panel.comments.empty')}</p>
          : (comments ?? []).map((c) => (
              <article key={c.id} className={`${styles.message} ${c.authorId === actor.id ? styles.mine : ''}`}>
                <header className={styles.head}>
                  <span className={styles.author}>{c.authorName}</span>
                  {tz && <CommentTime at={c.at} tz={tz} />}
                </header>
                {c.text && <p className={styles.body}>{c.text}</p>}
                {c.attachments.length > 0 && (
                  <div className={styles.attachments}>
                    {c.attachments.slice(0, 4).map((a, idx) => (
                      <span key={a.id} className={styles.thumb}>
                        <CommentPhoto photo={a} />
                        {idx === 3 && c.attachments.length > 4 && <span className={styles.more}>+{c.attachments.length - 4}</span>}
                      </span>
                    ))}
                  </div>
                )}
              </article>
            ))}
      </ScrollArea>

      {/* A comment is part of the promise's record: the server takes one only
          on a committed task (409 otherwise), so only there is it offered. */}
      {canExecute && !task?.committed && <p className={styles.gate}>{t('scheduler.panel.comments.uncommitted')}</p>}
      {canExecute && task?.committed && (
        <div className={styles.composer}>
          {photos.pending.length > 0 && (
            <div className={styles.pending}>
              {photos.pending.map((p) => (
                <PendingCommentPhoto key={p.key} photo={p} onRemove={() => photos.remove(p.key)} />
              ))}
            </div>
          )}
          {photos.failure && <p className={styles.error} role="alert">{t(PHOTO_REFUSAL_KEY[photos.failure])}</p>}
          <textarea
            className={styles.input}
            value={text}
            rows={2}
            placeholder={t('scheduler.panel.comments.placeholder')}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') send(); }}
          />
          <div className={styles.composerActions}>
            <button type="button" disabled={!photos.add} onClick={() => fileRef.current?.click()}>+ {t('scheduler.panel.comments.addPhoto')}</button>
            <input ref={fileRef} type="file" accept={PHOTO_ACCEPT} multiple hidden data-testid="comment-photo-input" onChange={onPickFiles} />
            <button type="button" className={styles.sendBtn} disabled={!canSend} onClick={send}>{t('scheduler.panel.comments.send')}</button>
          </div>
        </div>
      )}
    </div>
  );
}
